'use client';

import { createContext, Fragment, useContext, type ReactNode } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import type { Faq, Point } from './content';

// Presentational building blocks for the onboarding tour steps.

/**
 * Title of the item a search result pointed at. Blocks tag their items with
 * `data-search-title` so the tour can scroll to and flash them; FAQs also auto-expand.
 */
export const TourFocusContext = createContext<string | null>(null);

export function IconTile({ icon, color, size = 44 }: { icon: string; color?: string; size?: number }) {
  const theme = useTheme();
  const c = color ?? theme.palette.primary.main;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha(c, 0.12),
        color: c,
      }}
    >
      <Iconify icon={icon} width={Math.round(size * 0.55)} />
    </Box>
  );
}

export function StepHeader({
  icon,
  overline,
  title,
  intro,
  color,
}: {
  icon: string;
  overline: string;
  title: string;
  intro?: ReactNode;
  color?: string;
}) {
  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: intro ? 1.5 : 0 }}>
        <IconTile icon={icon} color={color} size={48} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: color ?? 'primary.main', lineHeight: 1.6 }}>
            {overline}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.25 }}>
            {title}
          </Typography>
        </Box>
      </Stack>
      {intro && (
        <Typography variant="body1" color="text.primary">
          {intro}
        </Typography>
      )}
    </Box>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      data-search-title={typeof children === 'string' ? children : undefined}
      variant="subtitle2"
      sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 12, fontWeight: 700, mt: 3.5, mb: 1.5 }}
    >
      {children}
    </Typography>
  );
}

export function PointGrid({ points, columns = 2, color }: { points: Point[]; columns?: 1 | 2 | 3; color?: string }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: columns === 1 ? '1fr' : '1fr 1fr', md: `repeat(${columns}, 1fr)` },
        gap: 1.5,
      }}
    >
      {points.map((p) => (
        <Stack key={p.title} data-search-title={p.title} direction="row" spacing={1.5} sx={{ p: 2, borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
          <Iconify icon={p.icon} width={22} sx={{ color: color ?? 'primary.main', flexShrink: 0, mt: 0.25 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">{p.title}</Typography>
            <Typography variant="body2" color="text.primary">
              {p.text}
            </Typography>
          </Box>
        </Stack>
      ))}
    </Box>
  );
}

/** Numbered left-to-right process (stacks vertically on small screens). */
export function FlowSteps({ steps, color }: { steps: Point[]; color?: string }) {
  const theme = useTheme();
  const c = color ?? theme.palette.primary.main;
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={0} alignItems="stretch">
      {steps.map((s, i) => (
        <Fragment key={s.title}>
          <Box
            data-search-title={s.title}
            sx={{
              flex: 1,
              minWidth: 0,
              p: 1.75,
              borderRadius: 1.5,
              border: 1,
              borderColor: 'divider',
              bgcolor: alpha(c, 0.03),
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: c,
                  color: theme.palette.getContrastText(c),
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </Box>
              <Iconify icon={s.icon} width={18} sx={{ color: c, flexShrink: 0 }} />
              <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
                {s.title}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.primary">
              {s.text}
            </Typography>
          </Box>
          {i < steps.length - 1 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: c,
                py: { xs: 0.25, md: 0 },
                px: { md: 0.5 },
              }}
            >
              <Iconify icon="solar:alt-arrow-right-linear" width={18} sx={{ transform: { xs: 'rotate(90deg)', md: 'none' } }} />
            </Box>
          )}
        </Fragment>
      ))}
    </Stack>
  );
}

export function Callout({
  tone = 'tip',
  title,
  children,
}: {
  tone?: 'tip' | 'info' | 'warning';
  title?: string;
  children: ReactNode;
}) {
  const theme = useTheme();
  const palette = { tip: theme.palette.success, info: theme.palette.info, warning: theme.palette.warning }[tone];
  const icon = { tip: 'solar:lightbulb-bolt-linear', info: 'solar:info-circle-linear', warning: 'solar:danger-triangle-linear' }[tone];
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        mt: 2.5,
        p: 2,
        borderRadius: 1.5,
        bgcolor: alpha(palette.main, 0.08),
        border: 1,
        borderColor: alpha(palette.main, 0.24),
      }}
    >
      <Iconify icon={icon} width={22} sx={{ color: palette.main, flexShrink: 0, mt: 0.25 }} />
      <Box>
        {title && <Typography variant="subtitle2">{title}</Typography>}
        <Typography variant="body2" color="text.primary">
          {children}
        </Typography>
      </Box>
    </Stack>
  );
}

export function FaqList({ items }: { items: Faq[] }) {
  const focus = useContext(TourFocusContext);
  return (
    // Spacing via `gap`, not child margins: the theme sets `margin: auto` on expanded accordions,
    // which would override Stack spacing and shrink/center the item inside a flex column.
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {items.map((f) => (
        <Accordion
          // Remount when targeted by search so defaultExpanded applies.
          key={`${f.q}-${focus === f.q}`}
          data-search-title={f.q}
          defaultExpanded={focus === f.q}
          disableGutters
          elevation={0}
          sx={{
            width: '100%',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            overflow: 'hidden',
            '&::before': { display: 'none' },
            '&.Mui-expanded': { margin: 0, borderColor: 'primary.main' },
          }}
        >
          <AccordionSummary expandIcon={<Iconify icon="solar:alt-arrow-down-linear" width={18} />}>
            <Typography variant="subtitle2">{f.q}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="body2" color="text.primary">
              {f.a}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

export function ChipRow({ items, mono }: { items: string[]; mono?: boolean }) {
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75}>
      {items.map((t) => (
        <Chip
          key={t}
          label={t}
          size="small"
          variant="outlined"
          sx={mono ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 } : undefined}
        />
      ))}
    </Stack>
  );
}

export function SimpleTable({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {head.map((h) => (
              <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow
              key={i}
              data-search-title={typeof r[0] === 'string' ? r[0] : undefined}
              sx={{ '&:last-of-type td': { borderBottom: 0 } }}
            >
              {r.map((cell, j) => (
                <TableCell key={j} sx={{ verticalAlign: 'top', ...(j === 0 && { fontWeight: 600, whiteSpace: 'nowrap' }) }}>
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Organization → sub-tenants → members, drawn with plain boxes. */
export function HierarchyDiagram({ orgName }: { orgName?: string }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const node = (label: string, sub: string, icon: string, strong?: boolean) => (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        border: 1,
        borderColor: strong ? primary : 'divider',
        bgcolor: strong ? alpha(primary, 0.08) : 'background.paper',
        textAlign: 'center',
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
        <Iconify icon={icon} width={18} sx={{ color: primary }} />
        <Typography variant="subtitle2" noWrap>
          {label}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.primary">
        {sub}
      </Typography>
    </Box>
  );
  const connector = <Box sx={{ width: 2, height: 16, bgcolor: 'divider', mx: 'auto' }} />;
  const tenants = [
    { label: 'Brand A', type: 'Standard', members: 3 },
    { label: 'Client B', type: 'Agency', members: 2 },
    { label: 'Region C', type: 'Enterprise', members: 4 },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: alpha(primary, 0.02) }}>
      <Box sx={{ maxWidth: 320, mx: 'auto' }}>
        {node(orgName || 'Your organization', 'Owner · org-level members · plan', 'mdi:office-building-outline', true)}
      </Box>
      {connector}
      <Box sx={{ position: 'relative', mx: { xs: 0, sm: '16.66%' }, height: 2, bgcolor: 'divider', display: { xs: 'none', sm: 'block' } }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
        {tenants.map((t) => (
          <Box key={t.label}>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{connector}</Box>
            {node(t.label, `${t.type} sub-tenant · own wallet`, 'mdi:domain')}
            {connector}
            <Stack direction="row" justifyContent="center" spacing={-0.75}>
              {Array.from({ length: t.members }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    border: 2,
                    borderColor: 'background.paper',
                    bgcolor: alpha(primary, 0.18 + i * 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: primary,
                  }}
                >
                  <Iconify icon="mdi:account" width={14} />
                </Box>
              ))}
            </Stack>
            <Typography variant="caption" color="text.primary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Scoped members
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** Worked agency-markup example: base cost + markup % = what the client sees. */
export function MarkupExample({ base = 100, percent = 30 }: { base?: number; percent?: number }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const markup = (base * percent) / 100;
  const tile = (label: string, value: string, strong?: boolean) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1.75,
        borderRadius: 1.5,
        textAlign: 'center',
        border: 1,
        borderColor: strong ? primary : 'divider',
        bgcolor: strong ? alpha(primary, 0.08) : 'transparent',
      }}
    >
      <Typography variant="caption" color="text.primary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ color: strong ? primary : 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  );
  const op = (symbol: string) => (
    <Typography variant="h5" sx={{ color: primary, px: { xs: 0, sm: 0.5 }, textAlign: 'center' }}>
      {symbol}
    </Typography>
  );
  return (
    <Box sx={{ p: 2, borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        Example: {percent}% markup on a ${base} campaign
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={1}>
        {tile('Base cost', `$${base}`)}
        {op('+')}
        {tile(`Markup (${percent}%)`, `$${markup}`)}
        {op('=')}
        {tile('Client sees', `$${base + markup}`, true)}
      </Stack>
    </Box>
  );
}

/** Available → Near → Over capacity scale for usage metrics. */
export function CapacityScale() {
  const theme = useTheme();
  const bands = [
    { label: 'Available', range: '0–79%', color: theme.palette.success.main, flex: 8 },
    { label: 'Near Capacity', range: '80–99%', color: theme.palette.warning.main, flex: 2 },
    { label: 'Over Capacity', range: '100%+ · overage', color: theme.palette.error.main, flex: 2 },
  ];
  return (
    <Box>
      <Stack direction="row" sx={{ height: 10, borderRadius: 5, overflow: 'hidden' }}>
        {bands.map((b) => (
          <Box key={b.label} sx={{ flex: b.flex, bgcolor: b.color }} />
        ))}
      </Stack>
      <Stack direction="row" sx={{ mt: 1 }}>
        {bands.map((b) => (
          <Box key={b.label} sx={{ flex: b.flex, minWidth: 0, pr: 1 }}>
            <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }} noWrap>
              {b.label}
            </Typography>
            <Typography variant="caption" color="text.primary" noWrap sx={{ display: 'block' }}>
              {b.range}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

/** Worked auto-recharge example: balance dips below threshold → top-up. */
export function AutoRechargeExample() {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const warn = theme.palette.warning.main;
  const bars = [620, 480, 310, 160, 90, 590, 450];
  const threshold = 100;
  const max = 650;
  return (
    <Box sx={{ p: 2, borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2">Example: threshold $100, recharge $500</Typography>
        <Typography variant="caption" color="text.primary">
          Balance over a week
        </Typography>
      </Stack>
      <Box sx={{ position: 'relative', height: 110 }}>
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: `${(threshold / max) * 100}%`,
            borderTop: `2px dashed ${warn}`,
            zIndex: 1,
          }}
        >
          <Typography variant="caption" sx={{ position: 'absolute', right: 0, top: -20, color: warn, fontWeight: 700 }}>
            Threshold $100
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ height: '100%' }}>
          {bars.map((v, i) => {
            const recharged = i === 5;
            return (
              <Box key={i} sx={{ flex: 1, height: `${(v / max) * 100}%`, position: 'relative' }}>
                <Box
                  sx={{
                    height: '100%',
                    borderRadius: '6px 6px 0 0',
                    bgcolor: v < threshold ? warn : alpha(primary, recharged ? 0.9 : 0.45),
                  }}
                />
                {recharged && (
                  <Typography
                    variant="caption"
                    sx={{ position: 'absolute', top: -20, left: '50%', transform: 'translateX(-50%)', fontWeight: 700, color: primary, whiteSpace: 'nowrap' }}
                  >
                    +$500
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
      <Typography variant="body2" color="text.primary" sx={{ mt: 1.5 }}>
        The balance falls to $90, below the $100 threshold, so $500 is charged to your default card automatically and
        campaigns keep running.
      </Typography>
    </Box>
  );
}
